import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";

const getValueByPath = (obj, path) => {
  if (!obj || !path) return undefined;

  return path.split(".").reduce((current, key) => {
    return current?.[key];
  }, obj);
};

export const useListCommon = ({
  isHavePagination = true,
  payload,
  onSuccess,
  onError,
  translation,
  target,
  queryKey,
  queryFn,
  customFieldHaveItems = null,
}) => {
  const { t } = useTranslation([translation]);
  const { addToast } = useToasts();

  const {
    params,
    key,
    isEnabled,
    staleTime,
    cacheTime,
    refetchOnMount,
    refetchOnWindowFocus,
  } = payload;

  const query = useQuery({
    queryKey: [...queryKey, params?.page || "all", key || "all"],
    queryFn,
    enabled: isEnabled || false,
    retry: 1,
    staleTime: staleTime !== undefined ? staleTime : 0,
    cacheTime: cacheTime !== undefined ? cacheTime : 5000,
    refetchOnMount: refetchOnMount !== undefined ? refetchOnMount : true,
    refetchOnWindowFocus:
      refetchOnWindowFocus !== undefined ? refetchOnWindowFocus : true,
    onSuccess,
    onError: (/** @type {any} */ error) => {
      const errorResponse = errorMessage(error);

      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.list", {
              target: t(target),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      const errorData = error?.response?.data;

      if (errorData.code === 422 && errorData.fields.length > 0) {
        errorData.fields.forEach(item => {
          switch (item.field) {
            default:
              addToast(
                <>
                  <div className="fs-4">{item.field}</div>
                  <p>{item.message}</p>
                </>,
                {
                  appearance: "error",
                  autoDismiss: true,
                }
              );
              break;
          }
        });
      }

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    /**
     * @param {{
     *  items: any[]
     *  _meta: {
     *    totalCount: number
     *  }
     *  _links: {
     *    self: {
     *      href: string
     *    }
     *  }
     * }} response
     */
    select: response => {
      // console.log("🚀 ~ useListCommon ~ select response:", response);
      if (isHavePagination) {
        const { items, _meta, _links } = response;
        return {
          items: items || [],
          params,
          totalCount: _meta?.totalCount || items?.length || 0,
          meta: _meta,
        };
      } else {
        // VD: customFieldHaveItems = "data.childs_hub" thì response sẽ là { data: { childs_hub: [...]} }
        // cần separate customFieldHaveItems => response?.["data"]["childs_hub"]
        let dataItems = customFieldHaveItems
          ? getValueByPath(response, customFieldHaveItems)
          : response;
        if (customFieldHaveItems === "data.childs_hub") {
          const parentHub = getValueByPath(response, "data.hub");
          dataItems = [parentHub, ...dataItems];
        }
        return {
          items: dataItems || [],
          params,
          totalCount: dataItems?.["length"] || 0,
        };
      }
    },
  });

  useEffect(() => {
    if (params?.run) {
      query.refetch();
    }
  }, [params]);

  return { query };
};

export const useMutationCommon = ({
  onSuccess,
  onError,
  mutationFn,
  target,
  translation,
  translationAction,
  isNotifyError = true,
  isNotifySuccess = true,
  isNeedCheckResponseResult = false,
}) => {
  const { t } = useTranslation([translation]);
  const { addToast } = useToasts();

  const mutation = useMutation({
    mutationFn,
    retry: 1,
    /**
     * @param {{
     *  data: any
     * }} response
     * @param {any} variables
     */
    onSuccess: (response, variables) => {
      const { data } = response;
      // console.log("🚀 ~ useMutationCommon ~ data:", data);
      let isSuccess = false;
      if (isNeedCheckResponseResult) {
        isSuccess = data?.result || data?.success || data?.status;
      } else {
        isSuccess = true;
      }
      if (!isSuccess) {
        if (isNotifyError) {
          addToast(
            t(`message.${translationAction}`, {
              target: t(target),
              status: t("common:message.error"),
            }),
            {
              appearance: "error",
              autoDismiss: true,
            }
          );
        }
        onError?.({ data: data || {}, variables });
        return;
      }
      if (isNotifySuccess) {
        addToast(
          t(`message.${translationAction}`, {
            target: t(target),
            status: t("common:message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      }

      if (typeof onSuccess === "function") {
        onSuccess({ data: data || {}, variables });
      }
    },
    onError: (/** @type {any} */ error, variables) => {
      const errorResponse = errorMessage(error);

      if (isNotifyError) {
        addToast(
          t(`message.${translationAction}`, {
            target: t(target),
            status: t("common:message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (errorResponse.fields?.length > 0) {
          const message = errorResponse.fields
            .map(field => field.message)
            .join("\n");
          addToast(message, {
            appearance: "error",
            autoDismiss: true,
          });
        }
      }

      if (typeof onError === "function") {
        onError(errorResponse, variables);
      }
    },
  });

  return { mutation };
};
